<template>
  <v-card class="fill-height d-flex flex-column" color="white">
    <v-card-title class="info-card-title">
      <v-icon left :color="isVip ? vipColor : 'primary'">{{ icon }}</v-icon>
      <span class="title-text" :class="{ 'is-vip': isVip }">{{ title }}</span>
    </v-card-title>
    <v-divider></v-divider>
    <v-card-text style="overflow-y: auto; flex: 1; padding-right: 8px;">
      <!-- Banner rectangular si hay imagen real -->
      <div v-if="avatarImage" class="pet-banner mb-3">
        <!-- Proporción fija con ancho máximo en px: se achica con el zoom igual que el texto y en tarjetas angostas, sin recortarse en una franja ni crecer con tarjetas anchas. -->
        <v-img
          :src="avatarImage"
          :alt="title"
          :aspect-ratio="16 / 9"
          max-width="373"
          cover
          class="mx-auto"
          style="border-radius: 8px;"
        />
      </div>
      <!-- Ícono fallback centrado si no hay imagen -->
      <div v-else-if="avatarIcon" class="d-flex justify-center mb-3 mt-1">
        <v-icon size="56" color="secondary">{{ avatarIcon }}</v-icon>
      </div>

      <!-- Grid de 3 columnas para items destacados -->
      <div v-if="gridItems && gridItems.length" class="grid-items mb-2">
        <div v-for="(item, index) in gridItems" :key="`grid-${index}`" class="grid-item">
          <div class="item-label">{{ item.label }}</div>
          <div class="item-value">{{ item.value }}</div>
        </div>
      </div>

      <!-- Grid de columnas secundario -->
      <div v-if="gridItems2 && gridItems2.length" class="grid-items-2 mb-2" :style="gridItems2Style">
        <template v-for="(item, index) in gridItems2">
          <!-- Divisor de ancho completo -->
          <div v-if="item.divider" :key="`divider-${index}`" class="grid-full-width">
            <v-divider class="my-2" />
          </div>
          <!-- Mini título de sección -->
          <div v-else-if="item.sectionTitle" :key="`section-${index}`" class="grid-full-width section-title">
            {{ item.sectionTitle }}
          </div>
          <!-- Comentario de usuario inline -->
          <div v-else-if="item.commentText" :key="`comment-${index}`" class="grid-full-width user-comments" style="margin: 8px 0;">
            <span style="font-weight: bold;">Comentarios del usuario:</span> {{ item.commentText }}
          </div>
          <!-- Item con label como hipervínculo (para adjuntos) -->
          <div v-else-if="item.labelLink" :key="`item-${index}`" class="grid-item">
            <a
              :href="item.link"
              target="_blank"
              rel="noopener noreferrer"
              class="attachment-link"
              @click.prevent="openAttachment(item.link, item.fetchFile)"
            >
              {{ item.label }}{{ item.visible === false ? ' (antiguo)' : '' }}
            </a>
          </div>
          <!-- Item normal -->
          <div v-else :key="`item-${index}`" class="grid-item">
            <div class="item-label" :style="item.bold ? 'font-weight: 700 !important;' : ''">{{ item.label }}</div>
            <div class="item-value" :style="[
              item.color ? `color: ${item.color} !important;` : '',
              item.bold ? 'font-weight: 700 !important; font-size: 14px !important;' : ''
            ].filter(Boolean).join(' ')">
              <a v-if="item.link" :href="item.link" target="_blank" rel="noopener noreferrer" class="grid-link">{{ item.value }}</a>
              <template v-else>{{ item.value }}</template>
            </div>
          </div>
        </template>
      </div>

      <p class="user-comments" v-if="description != null">
        <span style="font-weight: bold;">Comentarios del usuario:</span> {{ description }}
      </p>
      <v-list dense color="white">
        <template v-for="(item, index) in items">
        <v-divider v-if="item.divider" :key="`divider-${index}`" class="mt-4 mb-2" />
        <v-list-item :key="index" class="px-0">
          <v-list-item-content>
            <v-list-item-title class="item-label">
              <div v-if="item.link && !item.label.includes('Plan')">
                <span v-if="item.visible">
                  {{ item.label }}
                </span>
                <span v-else>
                  {{ `${item.label} (doc antiguo)` }}
                </span>
              </div>
              <div v-else>
                {{ `${item.label}` }}
              </div>
            </v-list-item-title>
            <v-list-item-subtitle
              style="white-space: pre-wrap; word-break: break-word; overflow: visible;"
              class="item-value"
            >
              <template v-if="item.link">
                <a
                  :href="item.link"
                  target="_blank"
                  rel="noopener noreferrer"
                  style="white-space: inherit; word-break: inherit;"
                >
                  {{ item.value || 'Ver adjunto' }}
                </a>
              </template>
              <template v-else-if="item.suffixLink && item.chip">
                <span>{{ item.value }}</span>
                <div style="display: flex; align-items: center; gap: 6px; margin-top: 4px;">
                  <v-chip
                    small dark
                    :color="item.chip.color"
                    style="cursor: pointer; font-size: 11px;"
                    @click.stop="$emit('chip-click', item)"
                  >{{ item.chip.label }}<v-icon right style="font-size: 12px; margin-left: 2px;">mdi-pencil</v-icon></v-chip>
                  <a :href="item.suffixLink.url" target="_blank" rel="noopener noreferrer">{{ item.suffixLink.text }}</a>
                </div>
              </template>
              <template v-else-if="item.suffixLink">
                {{ item.value }} · <a :href="item.suffixLink.url" target="_blank" rel="noopener noreferrer">{{ item.suffixLink.text }}</a>
              </template>
              <template v-else-if="item.chip">
                <span>{{ item.value }}</span>
                <div style="margin-top: 4px;">
                  <v-chip
                    small dark
                    :color="item.chip.color"
                    style="cursor: pointer; font-size: 11px;"
                    @click.stop="$emit('chip-click', item)"
                  >{{ item.chip.label }}<v-icon right style="font-size: 12px; margin-left: 2px;">mdi-pencil</v-icon></v-chip>
                </div>
              </template>
              <template v-else>
                {{ item.value }}
              </template>
            </v-list-item-subtitle>
          </v-list-item-content>
        </v-list-item>
        </template>
      </v-list>
      <v-img v-if="image" :src="image" max-width="100" class="mt-2"></v-img>
    </v-card-text>
  </v-card>
</template>

<script>
import { openAttachment } from '../utils/attachment-viewer.utils';

export default {
  name: 'info-card',
  props: {
    title: String,
    items: Array,
    gridItems: { type: Array, default: null },
    gridItems2: { type: Array, default: null },
    gridColumns2: { type: Number, default: 2 },
    icon: String,
    image: String,
    description: String,
    isVip: { type: Boolean, default: false },
    avatarIcon: { type: String, default: null },
    avatarImage: { type: String, default: null }
  },
  data() {
    return {
      // Naranja VIP. Fuera del theme porque solo lo usa esta tarjeta.
      vipColor: '#B85C00'
    };
  },
  computed: {
    gridItems2Style() {
      return { gridTemplateColumns: `repeat(${this.gridColumns2}, 1fr)` };
    }
  },
  methods: {
    openAttachment
  }
};
</script>

<style scoped>
/* Los valores de este componente son el origen de los tokens --pw-*
   (ver src/styles/variables.scss); acá solo se consumen. */
.fill-height {
  height: 100%;
}

.info-card-title {
  display: flex;
  align-items: center;
}

.title-text {
  color: var(--pw-primary);
  font-weight: 600;
}
.title-text.is-vip {
  color: #B85C00;
}

.item-label {
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.045em;
  color: var(--pw-text-muted);
  font-weight: 600;
  margin-bottom: 3px;
}

.item-value {
  font-size: 14px;
  color: var(--pw-text);
  font-weight: 500;
  opacity: 1;
}

.grid-items,
.grid-items-2 {
  display: grid;
  gap: 10px 8px;
  padding: 4px 0 10px;
  border-bottom: 1px solid var(--pw-divider);
  margin-bottom: 4px;
}

.grid-items {
  grid-template-columns: repeat(3, 1fr);
}

.grid-items-2 {
  grid-template-columns: repeat(2, 1fr);
}

.pet-banner {
  width: 100%;
  overflow: hidden;
  border-radius: 8px;
}

.grid-item {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.grid-item .item-value {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.grid-link,
.attachment-link {
  color: var(--pw-primary);
  text-decoration: underline;
  font-weight: 500;
}

.attachment-link {
  font-size: 14px;
  word-break: break-word;
  white-space: normal;
  display: block;
  line-height: 1.4;
  padding: 2px 0;
}

.grid-full-width {
  grid-column: 1 / -1;
}

.section-title {
  font-size: 11.5px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--pw-text-faint);
  font-weight: 600;
  margin-bottom: 3px;
}

.user-comments {
  font-size: 13px;
  font-weight: 400;
  color: var(--pw-text);
  margin-bottom: 0;
  line-height: 1.5;
}

.don-graf-card .v-list-item-subtitle {
  font-size: 18px !important;
  font-weight: 500 !important;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: var(--pw-text) !important;
}

.don-graf-card .v-list-item-title {
  font-size: 13px !important;
  margin-bottom: 4px;
}
</style>
